import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Row,
  Col,
  Alert,
  Table,
  Badge,
  Container,
  Card,
} from "react-bootstrap";
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  fetchPurchaseOrderById,
  fetchAvailableVendors,
  clearError,
  clearSuccess,
} from "../../redux/slices/inventorySlice";
import { fetchProducts } from "../../redux/slices/productSlice";
import { fetchVendorProfiles } from "../../redux/slices/vendorProfileSlice";
import { toast } from "react-toastify";
import { FaPencilAlt , FaRegTrashAlt , FaEye } from "react-icons/fa";

const PurchaseOrderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const {
    isCreatingPurchaseOrder,
    isUpdatingPurchaseOrder,
    isLoadingPurchaseOrder,
    error,
    success,
    currentPurchaseOrder,
  } = useSelector((state) => state.inventory);

  const { products = [] } = useSelector((state) => state.products);
  const { vendorProfiles = [] } = useSelector((state) => state.vendorProfile);
  const { availableVendors = [] } = useSelector((state) => state.inventory);

  const [formData, setFormData] = useState({
    vendor_id: "",
    purchase_date: new Date().toISOString().split("T")[0],
    total_amount: 0,
    payment_status: "unpaid",
    invoice_number: "",
  });

  const [purchaseItems, setPurchaseItems] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load products and vendors when component mounts
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchVendorProfiles());
    dispatch(fetchAvailableVendors());

    // Load purchase order details if editing
    if (isEditMode && id) {
      dispatch(fetchPurchaseOrderById(id));
    }
  }, [dispatch, isEditMode, id]);

  // Load purchase order data if in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      currentPurchaseOrder &&
      (currentPurchaseOrder.id === id || currentPurchaseOrder.id === parseInt(id))
    ) {
      console.log("Loading purchase order data:", currentPurchaseOrder);
      
      setFormData({
        vendor_id: currentPurchaseOrder.vendor_id
          ? String(currentPurchaseOrder.vendor_id)
          : "",
        purchase_date: currentPurchaseOrder.purchase_date
          ? new Date(currentPurchaseOrder.purchase_date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        total_amount: parseFloat(currentPurchaseOrder.total_amount) || 0,
        payment_status: currentPurchaseOrder.payment_status || "unpaid",
        invoice_number: currentPurchaseOrder.invoice_number || "",
      });

      // Handle both 'items' and 'purchase_items' field names
      const items =
        currentPurchaseOrder.items || currentPurchaseOrder.purchase_items || [];

      console.log("Purchase order items:", items);

      // Ensure items have the correct structure for editing
      const formattedItems = items.map((item) => ({
        product_id: item.product_id ? String(item.product_id) : "",
        quantity_purchased: parseInt(item.quantity_purchased) || 1,
        purchase_price_per_unit: parseFloat(item.purchase_price_per_unit) || 0,
        total_price: parseFloat(item.total_price) || 0,
      }));

      console.log("Formatted items:", formattedItems);
      setPurchaseItems(formattedItems);
    }
  }, [currentPurchaseOrder, isEditMode, id]);

  // Reset form when not in edit mode
  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode]);

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);

  // Handle toast messages for success and error (only once per message)
  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      // Handle both string and object error messages
      const errorMessage =
        typeof error === "string"
          ? error
          : error.message || "An error occurred";
      toast.error(errorMessage);
      lastErrorRef.current = error;
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success && success !== lastSuccessRef.current) {
      toast.success(success);
      lastSuccessRef.current = success;
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
    };
  }, [dispatch]);

  const resetForm = () => {
    setFormData({
      vendor_id: "",
      purchase_date: new Date().toISOString().split("T")[0],
      total_amount: 0,
      payment_status: "unpaid",
      invoice_number: "",
    });
    setPurchaseItems([]);
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const addPurchaseItem = () => {
    setPurchaseItems([
      ...purchaseItems,
      {
        product_id: "",
        quantity_purchased: 1,
        purchase_price_per_unit: 0,
        total_price: 0,
      },
    ]);
  };

  const removePurchaseItem = (index) => {
    const newItems = purchaseItems.filter((_, i) => i !== index);
    setPurchaseItems(newItems);
    calculateTotalAmount(newItems);
  };

  const updatePurchaseItem = (index, field, value) => {
    const newItems = [...purchaseItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Calculate total price for this item
    if (field === "quantity_purchased" || field === "purchase_price_per_unit") {
      const quantity =
        field === "quantity_purchased"
          ? value
          : newItems[index].quantity_purchased;
      const price =
        field === "purchase_price_per_unit"
          ? value
          : newItems[index].purchase_price_per_unit;
      newItems[index].total_price = quantity * price;
    }

    // Recalculate total price if product was changed
    if (field === "product_id") {
      const quantity = newItems[index].quantity_purchased;
      const price = newItems[index].purchase_price_per_unit;
      newItems[index].total_price = quantity * price;
    }

    // Clear error when product is changed (but don't validate yet)
    if (field === "product_id") {
      const errorKey = `item_${index}_price`;
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    // Inline validation: Check purchase price against selling price in real-time (only when purchase price is entered)
    if (field === "purchase_price_per_unit") {
      const errorKey = `item_${index}_price`;
      const currentItem = newItems[index];
      
      // Clear previous error first
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });

      // Validate if product is selected and purchase price is entered
      if (currentItem.product_id && currentItem.purchase_price_per_unit) {
        // Find product dynamically - handle both UUID strings and numeric IDs
        const selectedProduct = products.find(
          (p) => 
            p.id === currentItem.product_id || 
            p.id === parseInt(currentItem.product_id) || 
            String(p.id) === String(currentItem.product_id)
        );
        
        if (selectedProduct) {
          // Calculate selling price dynamically from product.price and product.discount_price
          const productPrice = parseFloat(selectedProduct.price || 0);
          const discountPrice = parseFloat(selectedProduct.discount_price || 0);
          const sellingPrice = productPrice - discountPrice;
          const purchasePrice = parseFloat(currentItem.purchase_price_per_unit) || 0;

          // Validate: purchase price cannot exceed selling price
          if (purchasePrice > sellingPrice) {
            setValidationErrors((prev) => ({
              ...prev,
              [errorKey]: `Purchase price cannot exceed selling price (₹${sellingPrice.toFixed(2)})`,
            }));
          }
        }
      }
    }

    setPurchaseItems(newItems);
    calculateTotalAmount(newItems);
  };

  const calculateTotalAmount = (items) => {
    const total = items.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setFormData((prev) => ({ ...prev, total_amount: total }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.vendor_id) {
      errors.vendor_id = "Vendor ID is required";
    }

    if (!formData.purchase_date) {
      errors.purchase_date = "Purchase date is required";
    }

    if (!formData.invoice_number) {
      errors.invoice_number = "Invoice number is required";
    }

    if (purchaseItems.length === 0) {
      errors.items = "At least one purchase item is required";
    }

    // Validate purchase items
    purchaseItems.forEach((item, index) => {
      if (!item.product_id) {
        errors[`item_${index}_product`] = "Product is required";
      }
      if (!item.quantity_purchased || item.quantity_purchased <= 0) {
        errors[`item_${index}_quantity`] = "Valid quantity is required";
      }
      if (!item.purchase_price_per_unit || item.purchase_price_per_unit <= 0) {
        errors[`item_${index}_price`] = "Valid price is required";
      } else if (item.product_id) {
        // Validate purchase price against product selling price (dynamic calculation)
        const selectedProduct = products.find(
          (p) => 
            p.id === item.product_id || 
            p.id === parseInt(item.product_id) || 
            String(p.id) === String(item.product_id)
        );
        if (selectedProduct) {
          // Calculate selling price dynamically from product.price and product.discount_price
          const productPrice = parseFloat(selectedProduct.price || 0);
          const discountPrice = parseFloat(selectedProduct.discount_price || 0);
          const sellingPrice = productPrice - discountPrice;
          
          if (item.purchase_price_per_unit > sellingPrice) {
            errors[
              `item_${index}_price`
            ] = `Purchase price cannot exceed selling price (₹${sellingPrice.toFixed(2)})`;
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      setIsSubmitting(true);

      const submitData = {
        ...formData,
        purchase_items: purchaseItems,
      };

      if (isEditMode) {
        await dispatch(
          updatePurchaseOrder({
            id: id,
            purchaseOrderData: submitData,
          })
        ).unwrap();
      } else {
        await dispatch(createPurchaseOrder(submitData)).unwrap();
      }

      setTimeout(() => {
        navigate("/purchase-orders");
      }, 1500);
    } catch (err) {
      console.error("Purchase order operation failed:", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/purchase-orders");
  };

  const isProcessing =
    isCreatingPurchaseOrder || isUpdatingPurchaseOrder || isSubmitting;

  // Show loading state when fetching purchase order data in edit mode
  if (isEditMode && isLoadingPurchaseOrder) {
    return (
      <div className="page-container">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading purchase order data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // Show error if purchase order not found in edit mode
  if (isEditMode && !isLoadingPurchaseOrder && !currentPurchaseOrder) {
    return (
      <div className="page-container">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <h5>Purchase order not found</h5>
              <p className="text-muted">
                The purchase order you're looking for doesn't exist.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate("/purchase-orders")}
              >
                Back to Purchase Orders
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container fluid>
        <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
          <h5 className="mb-0 fw-semibold text-light">
            {isEditMode ? "Update Product Purchase" : "Create Product Purchase"}
          </h5>
          <div>
            <Button
              variant="success"
              className="me-2 px-4"
              size="sm"
              type="submit"
              onClick={handleSubmit}
              disabled={isProcessing}
            >
              {isProcessing
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update"
                : "Save"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => dispatch(clearError())}
              >
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Vendor <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="vendor_id"
                      value={formData.vendor_id}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.vendor_id}
                      disabled={isProcessing}
                    >
                      <option value="">Select a vendor</option>
                      {Array.isArray(availableVendors) &&
                      availableVendors.length > 0
                        ? availableVendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.business_name} - {vendor.contact_person}
                            </option>
                          ))
                        : Array.isArray(vendorProfiles) &&
                          vendorProfiles.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.business_name} - {vendor.contact_person}
                            </option>
                          ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.vendor_id}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Invoice Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="invoice_number"
                      placeholder="Enter invoice number"
                      value={formData.invoice_number}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.invoice_number}
                      disabled={isProcessing}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.invoice_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Purchase Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="purchase_date"
                      value={formData.purchase_date}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.purchase_date}
                      disabled={isProcessing}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.purchase_date}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Status</Form.Label>
                    <Form.Select
                      name="payment_status"
                      value={formData.payment_status}
                      onChange={handleInputChange}
                      disabled={isProcessing}
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                      <option value="partial">Partial</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.total_amount}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h6 className="mt-4 mb-3">Purchase Items</h6>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  Add products to this purchase order
                </span>
                <Button
                  type="button"
                  variant="success"
                  size="sm"
                  onClick={addPurchaseItem}
                  disabled={isProcessing}
                >
                  {/* <i className="fas fa-plus me-1"></i> */}
                  Add Item
                </Button>
              </div>

              {validationErrors.items && (
                <Alert variant="danger" className="mb-3">
                  {validationErrors.items}
                </Alert>
              )}

              {purchaseItems.length > 0 && (
                <div className="table-responsive mb-3">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Purchase Price Per Unit</th>
                        <th>Total Price</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <Form.Select
                              value={item.product_id}
                              onChange={(e) =>
                                updatePurchaseItem(
                                  index,
                                  "product_id",
                                  e.target.value
                                )
                              }
                              isInvalid={
                                !!validationErrors[`item_${index}_product`]
                              }
                              disabled={isProcessing}
                            >
                              <option value="">Select Product</option>
                              {Array.isArray(products) &&
                                products.map((product) => (
                                  <option key={product.id} value={product.id}>
                                    {product.name} - ₹{`${parseFloat(product.price - product.discount_price).toFixed(2)}/${product.unit}`} (
                                    {product.stock_quantity || 0}/{product.unit} in stock)
                                  </option>
                                ))}
                            </Form.Select>
                            {validationErrors[`item_${index}_product`] && (
                              <div className="text-danger small">
                                {validationErrors[`item_${index}_product`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              min="1"
                              value={item.quantity_purchased}
                              onChange={(e) =>
                                updatePurchaseItem(
                                  index,
                                  "quantity_purchased",
                                  parseInt(e.target.value)
                                )
                              }
                              isInvalid={
                                !!validationErrors[`item_${index}_quantity`]
                              }
                              disabled={isProcessing}
                            />
                            {validationErrors[`item_${index}_quantity`] && (
                              <div className="text-danger small">
                                {validationErrors[`item_${index}_quantity`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.purchase_price_per_unit}
                              onChange={(e) =>
                                updatePurchaseItem(
                                  index,
                                  "purchase_price_per_unit",
                                  parseFloat(e.target.value)
                                )
                              }
                              isInvalid={
                                !!validationErrors[`item_${index}_price`]
                              }
                              disabled={isProcessing}
                            />
                            {validationErrors[`item_${index}_price`] && (
                              <div className="text-danger small">
                                {validationErrors[`item_${index}_price`]}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg="info">
                              ₹{item.total_price.toFixed(2)}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removePurchaseItem(index)}
                              disabled={isProcessing}
                            >
                              <FaRegTrashAlt />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default PurchaseOrderPage;
